import axios from 'axios';
import store from '../configure-store';
import { ShowSessionExpiredAction } from 'actions';
import { getGlobalAuth0Client } from '../helpers/auth-context';

// Declare constants from environment variables
declare const AUTH0_DOMAIN: string;
declare const AUTH0_CLIENT_ID: string;
declare const BASE_URL: string;
declare const EXTERNAL_URL: string;

// Utility to get the window location in a safe way
const getWindowLocation = () => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location;
  }
  return { href: '', origin: '' };
};

// Utility to get localStorage in a safe way
const getLocalStorage = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage;
  }
  return null;
};

// Function to log out the user and clear the session
export const logoutAndClearSession = () => {
  const { origin } = getWindowLocation();
  const returnTo = encodeURIComponent(origin);
  const logoutUrl = `https://${AUTH0_DOMAIN}/v2/logout?client_id=${AUTH0_CLIENT_ID}&returnTo=${returnTo}`;
  window.location.href = logoutUrl;
};

export interface IIdSkPayload {
  id: string;
  sk: string;
}

// Renew the ID token this many seconds before its exp claim, so a token
// that expires mid-flight is never sent.
const TOKEN_EXPIRY_LEEWAY_SECONDS = 60;

// Create a generic HTTP service instance
const createHttpService = (baseURL: string) => {
  const instance = axios.create({ baseURL });

  // The Authorization header is built from a token snapshot taken at boot
  // (getHeaderOptions), which goes stale in long-lived tabs. Re-resolve it
  // through the SDK before each authenticated request: getTokenSilently is a
  // cache hit while the token is valid and a silent renewal once it expires.
  instance.interceptors.request.use(async (config) => {
    if (config.headers?.Authorization) {
      const auth0Client = getGlobalAuth0Client();
      if (auth0Client) {
        try {
          await auth0Client.getTokenSilently();
          let claims = await auth0Client.getIdTokenClaims();
          // getTokenSilently only renews when the ACCESS token nears expiry,
          // and getIdTokenClaims returns the cached ID token with no expiry
          // check (auth0-spa-js v2 caches by access-token expires_in only).
          // Our API validates the ID token, whose TTL can be shorter (10h vs
          // 24h on this tenant), so force a renewal once the ID token itself
          // is expired or about to be. Concurrent forced renewals coalesce
          // inside the SDK (singlePromise keyed on clientId/audience/scope).
          if (
            claims?.exp &&
            claims.exp - TOKEN_EXPIRY_LEEWAY_SECONDS < Date.now() / 1000
          ) {
            await auth0Client.getTokenSilently({ cacheMode: 'off' });
            claims = await auth0Client.getIdTokenClaims();
          }
          if (claims?.__raw) {
            setGlobalIdToken(claims.__raw);
            config.headers.Authorization = `Bearer ${claims.__raw}`;
          }
        } catch (e) {
          // Renewal failed (e.g. Auth0 session ended). Send the request with
          // the existing token; the 401 response interceptor surfaces the
          // session-expired flow.
        }
      }
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        store.dispatch(ShowSessionExpiredAction());
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

// Export HTTP service instances
export const getHttpService = () => createHttpService(BASE_URL);
export const getHttpServiceAdmin = () => createHttpService(`${BASE_URL}/admin`);
export const getHttpServiceExternal = () => {
  const instance = createHttpService(EXTERNAL_URL);

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logoutAndClearSession();
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

// Define the type for header options
export interface IHeaderOption extends Record<string, string> {
  'content-type': string;
  Authorization: string;
}

// Global ID token cache (API expects ID token, not access token)
let globalIdToken: string | null = null;

export const setGlobalIdToken = (token: string) => {
  globalIdToken = token;
};

// Function to get header options with the authorization token
export const getHeaderOptions = (): IHeaderOption => {
  if (!globalIdToken) {
    throw new Error('ID token not available. Please ensure user is authenticated.');
  }
  
  return {
    'content-type': 'application/json',
    Authorization: `Bearer ${globalIdToken}`,
  };
};

